"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/navigation";

export interface OvermoldingToolingSolutionsProps {
	/** 命名空间需含 title、cardProto*、cardProd*；若 showIntro 为 true，还需 introDesc */
	translationNamespace?: string;
	/** 标题与双卡之间是否展示 introDesc 段落（嵌件注塑等页专用） */
	showIntro?: boolean;
	/** 「Learn More」站内路径：Injection Mold Tooling 服务页 slug */
	learnMoreHref?: string;
	/** 「Get a quote」外链 */
	quoteHref?: string;
}

const DEFAULT_LEARN_MORE = "/injection-mold-tooling";
const DEFAULT_QUOTE_HREF = "/upload";

/**
 * Overmolding Tooling Solutions：标题 + 细长高绿条（与标题底对齐）+ 左右双卡（原型灰底 / 量产淡绿底）
 */
export default function OvermoldingToolingSolutions({
	translationNamespace = "overmoldingToolingSolutionsSection",
	showIntro = false,
	learnMoreHref = DEFAULT_LEARN_MORE,
	quoteHref = DEFAULT_QUOTE_HREF,
}: OvermoldingToolingSolutionsProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="relative overflow-hidden bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			{/* 左侧点阵装饰（与设计稿一致，不阻挡点击） */}
			<div
				className="pointer-events-none absolute -left-2 top-1/4 hidden h-56 w-36 -translate-y-1/2 select-none opacity-[0.35] sm:block md:left-0 md:h-72 md:w-44"
				style={{
					backgroundImage: "radial-gradient(circle, #c4c4c4 1.5px, transparent 1.5px)",
					backgroundSize: "14px 14px",
				}}
				aria-hidden
			/>

			<div className="container relative px-4 sm:px-6">
				<div className="mb-6 flex flex-wrap items-end justify-center gap-3 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<div
						className="h-px w-28 shrink-0 rounded-full bg-themePrimary400 sm:h-[2px] sm:w-36 md:w-44"
						aria-hidden
					/>
				</div>

				{showIntro ? (
					<p className="mx-auto mb-10 text-center text-sm leading-relaxed text-black md:text-base md:mb-12">
						{t("introDesc")}
					</p>
				) : null}

				<div className="mx-auto grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
					{/* 左卡：原型 / 快速模具 */}
					<article className="flex min-h-full flex-col border border-gray-100 bg-[#f5f5f5] p-6 md:p-8">
						<p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
							{t("cardProtoLabel")}
						</p>
						<h3 className="mt-2 text-lg font-bold text-black sm:text-xl md:text-2xl">{t("cardProtoTitle")}</h3>
						<p className="mt-4 flex-1 text-left text-sm leading-relaxed text-black md:text-base">
							{t("cardProtoDesc")}
						</p>
						<Link
							href={learnMoreHref}
							className="group mt-8 inline-flex w-fit items-center justify-center rounded-lg bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-8 sm:py-3.5 sm:text-base"
						>
							{t("cardProtoButton")}
						</Link>
					</article>

					{/* 右卡：量产模具 */}
					<article className="flex min-h-full flex-col border border-[#e8f5d0] bg-[#f4fbe1] p-6 md:p-8">
						<p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
							{t("cardProdLabel")}
						</p>
						<h3 className="mt-2 text-lg font-bold text-black sm:text-xl md:text-2xl">{t("cardProdTitle")}</h3>
						<p className="mt-4 flex-1 text-left text-sm leading-relaxed text-black md:text-base">
							{t("cardProdDesc")}
						</p>
						<a
							href={quoteHref}
							target="_blank"
							rel="noopener noreferrer"
							className="group mt-8 inline-flex w-fit items-center justify-center rounded-lg bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-8 sm:py-3.5 sm:text-base"
						>
							{t("cardProdButton")}
						</a>
					</article>
				</div>
			</div>
		</section>
	);
}
